import { logger } from './logger';

/**
 * sessionStorage keys and event name of the "stale build recovery" script,
 * public/stale-build-recovery.js (DEV-7884) — keep both places in sync.
 */
export const STALE_BUILD_RELOAD_AT_KEY = 'portal-stale-build-reload-at';
export const STALE_BUILD_RELOAD_REASON_KEY = 'portal-stale-build-reload-reason';
export const STALE_BUILD_RELOAD_BLOCKED_EVENT = 'portal-stale-build-reload-blocked';

/**
 * Reports the stale-build recovery to Datadog, so a release's chunk errors can be split into
 * recovered and not recovered sessions:
 * - once, that this page load comes from an automatic reload. Logged after boot rather than
 *   before the reload, where the page unload could drop it. `reloadedAt` shows a report delayed
 *   to a later boot of the same tab;
 * - each time the loop guard suppresses a reload, the client then seeing the error screen. Both
 *   are info: the failure itself is logged as an error by the ErrorBoundary.
 *
 * Only the failing asset file name is logged (no PII). The reload timestamp is left in place:
 * it is the loop guard. Call after Datadog is set up; returns the listener's removal.
 */
export function setupStaleBuildRecoveryReporting(): () => void {
  const onBlocked = (event: Event) => {
    logger.info('Stale-build reload suppressed by loop guard', {
      asset: (event as CustomEvent<string>).detail,
    });
  };
  window.addEventListener(STALE_BUILD_RELOAD_BLOCKED_EVENT, onBlocked);
  reportReload();

  return () => window.removeEventListener(STALE_BUILD_RELOAD_BLOCKED_EVENT, onBlocked);
}

function reportReload(): void {
  let asset: string | null;
  let reloadedAt: string | null;
  try {
    asset = sessionStorage.getItem(STALE_BUILD_RELOAD_REASON_KEY);
    if (asset === null) {
      return;
    }
    sessionStorage.removeItem(STALE_BUILD_RELOAD_REASON_KEY);
    reloadedAt = sessionStorage.getItem(STALE_BUILD_RELOAD_AT_KEY);
  } catch {
    return;
  }
  logger.info('Portal reloaded after a stale-build asset failure', {
    asset,
    reloadedAt: reloadedAt ? new Date(Number(reloadedAt)).toISOString() : null,
  });
}
